import { useEffect, useState } from 'react';
import { BaseTranslations } from '../../../../baseTranslations';
import { PushDataLayerFn } from '../../../../types/pushDataLayer';
import { isVehicleSelectionComplete } from '../../../../utils/carInsurance/isVehicleSelectionComplete';
import {
  EMPTY_FROM_MAKE,
  EMPTY_FROM_MODEL,
  EMPTY_FROM_VARIANT,
  EMPTY_FROM_YEAR,
} from '../../../../utils/carInsurance/moneymaxVehicleFields';
import { useCmsClientContext } from '../../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../../utils/translations/getClientTranslations';
import Button from '../../../Button';
import { MaterialIcon } from '../../../MaterialIcon';
import { carUseType, nextButtonStyles } from '../../constants';
import { useSafeCarInsuranceContext } from '../../context/useSafeCarInsurance';
import { FormSelect } from '../../Form/FormSelect';
import { VEHICLE_TRANSLATIONS } from '../../i18n/vehicle';
import { Checkbox } from '../../Inputs/Checkbox';
import { TabsCheckbox } from '../../Inputs/TabsCheckbox';
import { StepHeader } from '../StepHeader';

type VehicleDetailsMoneymaxProps = {
  onNext: () => void;
  pushDataLayer: PushDataLayerFn;
};

export const VehicleDetailsMoneymax: React.FC<VehicleDetailsMoneymaxProps> = ({
  onNext,
  pushDataLayer,
}) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const vehicleText = getClientTranslations(VEHICLE_TRANSLATIONS, context);
  const carInsuranceContext = useSafeCarInsuranceContext();
  const [submitted, setSubmitted] = useState(false);
  const vehicleValue = carInsuranceContext?.requestParams.vehicle.value;
  const isComplete = !!vehicleValue && isVehicleSelectionComplete(vehicleValue);
  // The errors belong to one blocked "Next": clear them once the vehicle is
  // complete, so a later emptied field does not show them again (CMS-2047).
  useEffect(() => {
    if (isComplete) setSubmitted(false);
  }, [isComplete]);
  if (!carInsuranceContext) {
    return null;
  }
  const {
    requestParams: {
      vehicle: { value, setValue },
    },
    data: {
      vehicleMakers,
      vehicleModels,
      vehicleTrims,
      isTrimsLoading,
      isVehiclesDataLoading,
      yearsOptions,
    },
  } = carInsuranceContext;

  const { makeName, manufactureYear, modelCode, trim, type } = value;

  const updateVehicle = (updated: Partial<typeof value>) => {
    setValue({
      ...value,
      ...updated,
    });
  };

  const trackFormSelect = (title: string, val: string) => {
    pushDataLayer({
      event: 'form_interact',
      lead_form_name: 'ci-policy-search-module-car-info',
      action: 'select',
      component_type: 'dropdown',
      component_title: title,
      vertical_code: 'CI',
      form_field_value: val,
    });
  };

  const changePrimaryUseCar = (val: string) => {
    updateVehicle({ type: val });
  };

  const handleNext = () => {
    if (isComplete) {
      onNext();
    } else {
      setSubmitted(true);
    }
  };

  const showError = (fieldValue: string | number, error: string) =>
    submitted && !fieldValue ? error : undefined;

  const selects = [
    {
      options: vehicleMakers.value,
      label: staticText.brand,
      placeholder: vehicleText.select_make,
      error: showError(makeName, vehicleText.select_your_make),
      value: makeName,
      onChange: (val: string) => {
        trackFormSelect('Vehicle make', val);
        updateVehicle({
          makeName: val,
          ...(val !== makeName && EMPTY_FROM_MAKE),
        });
      },
    },
    {
      options: vehicleModels.value,
      disabled: isVehiclesDataLoading,
      label: staticText.model,
      placeholder: vehicleText.select_model,
      error: showError(modelCode, vehicleText.select_your_model),
      value: modelCode,
      onChange: (val: string) => {
        const modelName =
          vehicleModels.value.find((i) => i.value === val)?.label.trim() ?? '';
        trackFormSelect('Vehicle model', val);
        updateVehicle({
          modelCode: val,
          modelName,
          ...(val !== modelCode && EMPTY_FROM_MODEL),
        });
      },
    },
    {
      options: yearsOptions.value,
      label: staticText.year_of_manufacture,
      placeholder: vehicleText.select_year,
      error: showError(manufactureYear, vehicleText.select_your_year),
      value: manufactureYear.toString(),
      onChange: (val: string) => {
        trackFormSelect('Year of manufacture', val);
        updateVehicle({
          manufactureYear: val,
          ...(val !== manufactureYear.toString() && EMPTY_FROM_YEAR),
        });
      },
    },
    {
      options: vehicleTrims.value,
      disabled: isVehiclesDataLoading || isTrimsLoading,
      label: staticText.variant,
      placeholder: vehicleText.select_variant,
      error: showError(trim, vehicleText.select_your_variant),
      value: trim,
      onChange: (val: string) =>
        updateVehicle({
          trim: val,
          ...(val !== trim && EMPTY_FROM_VARIANT),
        }),
    },
  ];

  return (
    <div
      className="mt-4 flex w-full flex-col gap-4"
      data-component="vehicle-details-moneymax"
    >
      <StepHeader
        title={staticText.vehicle_details}
        subTitle={staticText.vehicle_details_subtitle}
        stepNumber={1}
      />

      {selects.map((props) => (
        <FormSelect key={props.label} {...props} />
      ))}

      <div>
        <p className="font-base mb-1 font-medium text-gray-700 text-base">
          <span className="flex items-center gap-1">
            {staticText.primary_use_car}
          </span>
        </p>
        <TabsCheckbox
          options={carUseType}
          onChange={(val) => changePrimaryUseCar(val)}
          value={type}
        />
      </div>

      <div className="flex items-center gap-2">
        <Checkbox containerClass="!size-5" />
        <span className="font-medium text-gray-900 text-sm">
          {staticText.interested_ctpl}
        </span>
      </div>

      <Button className={nextButtonStyles} onClick={handleNext}>
        {staticText.next}
        <MaterialIcon
          icon="arrow_right_alt"
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          className="text-white text-[22px]"
        />
      </Button>
    </div>
  );
};
